/**
 * NewUserPage.jsx  —  /admin/users/new
 *
 * Tam-sayfa kullanici olusturma akisi (modal degil).
 *
 * Adim 0  — Rol Secimi: Ogrenci / Egitmen / Admin
 *           URL query ile on-secim: ?role=student
 *
 * Ogrenci icin  → 5 adim (Kisisel Bilgi → Ana Kurs → Yan Kurslar → Sertifika → Ozet+Odeme)
 * Egitmen/Admin → 1 adim  (Kisisel Bilgi) → kaydet
 *
 * Basari ekraninda:
 *   - Uretilen sifre gosterimi
 *   - WhatsApp / SMS / E-posta bildirim butonlari (AdminStudentEnroll icindeki blok)
 *   - "Listeye Don" (role gore /admin/users ya da /admin/students)
 *   - "Yeni Kayit" (formu sifirla)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAccountingBanks } from '../../api/queries/accounting';
// FAZ 1 satış sadeleştirme (2026-09-25) — satış anında POS hesabı tahsilat
// hesabı olarak KABUL EDİLMEZ, yalnızca kasa/banka listelenir.
import { isPosAccountType } from '../../lib/posNetAmount';
// FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan, bkz.
// components/sales/PaymentPlanFields.jsx dosya başı yorumu.
import PaymentPlanFields from '../../components/sales/PaymentPlanFields';
import {
  Users,
  GraduationCap,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Check,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  RefreshCw,
  BookOpen,
  Award,
  CreditCard,
  Plus,
  Trash2,
  Search,
  MessageSquare,
  Phone,
  Mail,
  Send,
  Gift,
  UserPlus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── helpers ───────────────────────────────────────────────────────────────────

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

const categoryFilters = [
  { id: 'cabin_crew', label_tr: 'Kabin Memurluğu', label: 'Cabin Crew' },
  { id: 'programming', label_tr: 'Programlama', label: 'Programming' },
  { id: 'design', label_tr: 'Tasarım', label: 'Design' },
  { id: 'business', label_tr: 'İş Dünyası', label: 'Business' },
  { id: 'marketing', label_tr: 'Pazarlama', label: 'Marketing' },
  { id: 'language', label_tr: 'Diller', label: 'Languages' },
  { id: 'health', label_tr: 'Sağlık', label: 'Health' },
];

const INITIAL_INFO = {
  first_name: '', last_name: '', tc_no: '', birth_date: '',
  email: '', phone: '', address: '', city: '', district: '',
  postal_code: '', emergency_contact: '', emergency_phone: '', notes: '',
  autoGenPassword: true, generatedPassword: '', password: '',
  kvkk_consent: false,
};

// FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan artık paylaşılan
// components/sales/PaymentPlanFields.jsx tarafından yönetilir (bkz. o
// dosyanın başı yorumu); `payment` burada yalnızca indirim/not/ücretsiz
// atama gibi PaymentPlanFields DIŞI alanları taşır.
const INITIAL_PAYMENT = {
  notes: '', discount_percent: 0,
  assignment_type: 'paid',
};

// ── Success screen ─────────────────────────────────────────────────────────────

function SuccessScreen({ createdUser, enrollResult, selectedRole, lang, authHeaders, onReset, navigate }) {
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});

  const sendNotification = async (channel) => {
    setSending(prev => ({ ...prev, [channel]: true }));
    try {
      const res = await axios.post(`${API}/admin/notify/registration`, {
        user_id: createdUser.user_id,
        channels: [channel],
        password: createdUser.generated_password || createdUser.generatedPassword || '***',
      }, { headers: authHeaders() });
      const status = res.data.results?.[channel]?.status;
      setSent(prev => ({ ...prev, [channel]: status || 'sent' }));
    } catch {
      setSent(prev => ({ ...prev, [channel]: 'error' }));
    } finally {
      setSending(prev => ({ ...prev, [channel]: false }));
    }
  };

  const sendAll = async () => {
    for (const ch of ['whatsapp', 'sms', 'email']) {
      await sendNotification(ch);
    }
  };

  const listPath = selectedRole === 'student' ? '/admin/students' : '/admin/personel-yonetimi';
  const listLabel = selectedRole === 'student'
    ? (lang === 'tr' ? 'Öğrenci Listesi' : 'Student List')
    : (lang === 'tr' ? 'Personel Listesi' : 'Personnel List');

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl mx-auto space-y-4">
      {/* Header card */}
      <div className="card-clay p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={40} className="text-white" />
        </div>
        <h2 className="font-display text-2xl font-bold mb-2">
          {lang === 'tr' ? 'Kayıt Başarılı!' : 'Registration Complete!'}
        </h2>
        <p className="text-[var(--text-muted)]">
          {createdUser?.name || `${createdUser?.first_name || ''} ${createdUser?.last_name || ''}`.trim()}
          {enrollResult && ` — ${enrollResult.enrolled_courses?.length || 0} ${lang === 'tr' ? 'kurs atandı' : 'courses enrolled'}`}
        </p>
      </div>

      {/* Generated credentials */}
      {(createdUser?.generated_password || createdUser?.generatedPassword) && (
        <div className="card-clay p-5 bg-orange-50 border-2 border-orange-200" data-testid="generated-password">
          <h3 className="font-bold text-sm text-orange-700 mb-3">
            {lang === 'tr' ? 'Giriş Bilgileri' : 'Login Credentials'}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold text-[var(--text-muted)] mb-1">E-posta</p>
              <p className="font-mono font-bold text-sm bg-white px-3 py-1.5 rounded-lg border border-orange-200">
                {createdUser.email}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-[var(--text-muted)] mb-1">
                {lang === 'tr' ? 'Şifre' : 'Password'}
              </p>
              <p className="font-mono font-bold text-sm bg-white px-3 py-1.5 rounded-lg border border-orange-200">
                {createdUser.generated_password || createdUser.generatedPassword}
              </p>
            </div>
          </div>
          <p className="text-[10px] text-orange-600 mt-2">
            {lang === 'tr'
              ? 'Bu bilgileri not alın veya aşağıdaki bildirimlerle kullanıcıya gönderin.'
              : 'Save these credentials or send via notifications below.'}
          </p>
        </div>
      )}

      {/* Notifications */}
      <div className="card-clay p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-sm">
            {lang === 'tr' ? 'Bildirim Gönder' : 'Send Notifications'}
          </h3>
          <button
            onClick={sendAll}
            className="btn-primary px-4 py-1.5 text-xs flex items-center gap-1"
            data-testid="send-all-notifications-btn"
          >
            <Send size={12} /> {lang === 'tr' ? 'Tümünü Gönder' : 'Send All'}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { ch: 'whatsapp', icon: MessageSquare, label: 'WhatsApp', color: 'green' },
            { ch: 'sms', icon: Phone, label: 'SMS', color: 'blue' },
            { ch: 'email', icon: Mail, label: 'E-posta', color: 'purple' },
          ].map(n => (
            <button
              key={n.ch}
              onClick={() => sendNotification(n.ch)}
              disabled={sending[n.ch] || !!sent[n.ch]}
              className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all disabled:opacity-40 bg-${n.color}-50 border-${n.color}-200 text-${n.color}-600`}
              data-testid={`send-${n.ch}-btn`}
            >
              <n.icon size={12} className="inline mr-1" />
              {sent[n.ch] ? (sent[n.ch] === 'error' ? (lang === 'tr' ? 'Hata' : 'Error') : (lang === 'tr' ? 'Gönderildi' : 'Sent')) : sending[n.ch] ? '...' : n.label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-center">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onReset}
          className="btn-primary px-6 py-3"
          data-testid="new-registration-btn"
        >
          {lang === 'tr' ? 'Yeni Kayıt' : 'New Registration'}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate(listPath)}
          className="btn-tactile px-6 py-3 bg-white border-[var(--border-light)]"
          data-testid="back-to-list-btn"
        >
          {listLabel}
        </motion.button>
      </div>
    </motion.div>
  );
}

// ── Step 0: Role selection ─────────────────────────────────────────────────────

function RoleStep({ lang, onSelect }) {
  const roles = [
    {
      value: 'student',
      icon: GraduationCap,
      labelTr: 'Öğrenci',
      labelEn: 'Student',
      descTr: 'Kurs, sertifika ve ödeme akışıyla tam kayıt',
      descEn: 'Full enrollment with course, certificate and payment flow',
      color: 'cyan',
    },
    {
      value: 'instructor',
      icon: BookOpen,
      labelTr: 'Eğitmen',
      labelEn: 'Instructor',
      descTr: 'Yalnızca kişisel bilgiler — kurs akışı yok',
      descEn: 'Personal info only — no course flow',
      color: 'emerald',
    },
    {
      value: 'admin',
      icon: ShieldCheck,
      labelTr: 'Admin',
      labelEn: 'Admin',
      descTr: 'Admin yetkisiyle kullanıcı oluştur',
      descEn: 'Create user with admin privileges',
      color: 'amber',
    },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="card-clay p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[var(--brand-primary)] flex items-center justify-center mx-auto mb-4">
            <UserPlus size={28} className="text-white" />
          </div>
          <h2 className="font-display text-2xl font-bold mb-2">
            {lang === 'tr' ? 'Yeni Kullanıcı Oluştur' : 'Create New User'}
          </h2>
          <p className="text-[var(--text-muted)] text-sm">
            {lang === 'tr' ? 'Oluşturmak istediğiniz kullanıcı tipini seçin' : 'Select the type of user to create'}
          </p>
        </div>
        <div className="grid gap-4">
          {roles.map(role => {
            const Icon = role.icon;
            const colorMap = {
              cyan: 'border-cyan-200 hover:border-cyan-400 hover:bg-cyan-50',
              emerald: 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50',
              amber: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50',
            };
            const iconBg = {
              cyan: 'bg-cyan-100 text-cyan-600',
              emerald: 'bg-emerald-100 text-emerald-600',
              amber: 'bg-amber-100 text-amber-600',
            };
            return (
              <motion.button
                key={role.value}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => onSelect(role.value)}
                className={`flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all ${colorMap[role.color]}`}
                data-testid={`role-select-${role.value}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg[role.color]}`}>
                  <Icon size={22} />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-base mb-0.5">
                    {lang === 'tr' ? role.labelTr : role.labelEn}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {lang === 'tr' ? role.descTr : role.descEn}
                  </p>
                </div>
                <ChevronRight size={20} className="text-[var(--text-muted)] flex-shrink-0" />
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Personal info form (shared Step 1) ────────────────────────────────────────

// Module-level helper — sabit referans, re-render'da unmount/remount olmaz
function personalInputClass(errors, key) {
  return `w-full px-3 py-2.5 rounded-xl border-2 text-sm font-semibold focus:outline-none transition-colors ${
    errors[key] ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-white focus:border-cyan-400'
  }`;
}

// Module-level Field component — render gövdesi dışında tanımlandığı için
// her re-render'da yeni fonksiyon referansı oluşmaz; focus kaybı yaşanmaz.
function PersonalInfoField({ labelKey, fieldKey, type = 'text', required = false, info, setInfo, errors }) {
  return (
    <div>
      <label htmlFor={`nup-${fieldKey}`} className="block text-xs font-bold text-gray-600 mb-1">
        {labelKey} {required && <span className="text-rose-500">*</span>}
      </label>
      <input
        id={`nup-${fieldKey}`}
        type={type}
        value={info[fieldKey] || ''}
        onChange={e => setInfo(prev => ({ ...prev, [fieldKey]: e.target.value }))}
        className={personalInputClass(errors, fieldKey)}
        aria-required={required}
        aria-invalid={!!errors[fieldKey]}
      />
      {errors[fieldKey] && (
        <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
          <AlertCircle size={12} /> {errors[fieldKey]}
        </p>
      )}
    </div>
  );
}

function PersonalInfoStep({ info, setInfo, errors, lang, selectedRole }) {
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (info.autoGenPassword && !info.generatedPassword) {
      setInfo(prev => ({ ...prev, generatedPassword: generatePassword() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card-clay p-6 space-y-5">
      <h3 className="font-display text-xl font-bold flex items-center gap-2">
        <Users size={22} className="text-[var(--brand-primary)]" />
        {lang === 'tr' ? 'Kişisel Bilgiler' : 'Personal Information'}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PersonalInfoField labelKey={lang === 'tr' ? 'Ad' : 'First Name'} fieldKey="first_name" required info={info} setInfo={setInfo} errors={errors} />
        <PersonalInfoField labelKey={lang === 'tr' ? 'Soyad' : 'Last Name'} fieldKey="last_name" required info={info} setInfo={setInfo} errors={errors} />
        <PersonalInfoField labelKey="E-posta" fieldKey="email" type="email" required info={info} setInfo={setInfo} errors={errors} />
        <PersonalInfoField labelKey={lang === 'tr' ? 'Telefon' : 'Phone'} fieldKey="phone" required info={info} setInfo={setInfo} errors={errors} />
        {selectedRole === 'student' && (
          <>
            <PersonalInfoField labelKey="TC Kimlik No" fieldKey="tc_no" required info={info} setInfo={setInfo} errors={errors} />
            <PersonalInfoField labelKey={lang === 'tr' ? 'Doğum Tarihi' : 'Birth Date'} fieldKey="birth_date" type="date" info={info} setInfo={setInfo} errors={errors} />
            <PersonalInfoField labelKey={lang === 'tr' ? 'Şehir' : 'City'} fieldKey="city" info={info} setInfo={setInfo} errors={errors} />
            <PersonalInfoField labelKey={lang === 'tr' ? 'İlçe' : 'District'} fieldKey="district" info={info} setInfo={setInfo} errors={errors} />
            <PersonalInfoField labelKey={lang === 'tr' ? 'Posta Kodu' : 'Postal Code'} fieldKey="postal_code" info={info} setInfo={setInfo} errors={errors} />
            <PersonalInfoField labelKey={lang === 'tr' ? 'Acil Durum Kişisi' : 'Emergency Contact'} fieldKey="emergency_contact" info={info} setInfo={setInfo} errors={errors} />
            <PersonalInfoField labelKey={lang === 'tr' ? 'Acil Tel' : 'Emergency Phone'} fieldKey="emergency_phone" info={info} setInfo={setInfo} errors={errors} />
          </>
        )}
      </div>

      {selectedRole === 'student' && (
        <>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">
              {lang === 'tr' ? 'Adres' : 'Address'}
            </label>
            <textarea
              rows={2}
              value={info.address || ''}
              onChange={e => setInfo(prev => ({ ...prev, address: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm font-semibold focus:border-cyan-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">
              {lang === 'tr' ? 'Notlar' : 'Notes'}
            </label>
            <textarea
              rows={2}
              value={info.notes || ''}
              onChange={e => setInfo(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm font-semibold focus:border-cyan-400 focus:outline-none"
            />
          </div>
        </>
      )}

      {/* Password */}
      <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200 space-y-3">
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">
          {lang === 'tr' ? 'Şifre' : 'Password'}
        </p>
        <div className="flex gap-3">
          {[
            { v: true, label: lang === 'tr' ? 'Otomatik Üret' : 'Auto-generate' },
            { v: false, label: lang === 'tr' ? 'Manuel Gir' : 'Manual' },
          ].map(opt => (
            <button
              key={String(opt.v)}
              type="button"
              onClick={() => setInfo(prev => ({
                ...prev,
                autoGenPassword: opt.v,
                generatedPassword: opt.v ? (prev.generatedPassword || generatePassword()) : prev.generatedPassword,
              }))}
              className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${
                info.autoGenPassword === opt.v ? 'bg-cyan-50 border-cyan-400 text-cyan-700' : 'bg-white border-gray-200 text-gray-500'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {info.autoGenPassword ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border-2 border-dashed border-cyan-300 rounded-xl px-3 py-2 text-sm font-mono font-bold text-cyan-700 tracking-wider">
              {info.generatedPassword || '—'}
            </code>
            <button
              type="button"
              onClick={() => setInfo(prev => ({ ...prev, generatedPassword: generatePassword() }))}
              className="p-2 rounded-xl bg-cyan-100 hover:bg-cyan-200 text-cyan-700 transition-colors"
              title={lang === 'tr' ? 'Yeniden Üret' : 'Regenerate'}
            >
              <RefreshCw size={16} />
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              value={info.password || ''}
              onChange={e => setInfo(prev => ({ ...prev, password: e.target.value }))}
              placeholder={lang === 'tr' ? 'Şifre girin...' : 'Enter password...'}
              className={`w-full px-3 py-2.5 pr-10 rounded-xl border-2 text-sm font-semibold focus:outline-none transition-colors ${
                errors.password ? 'border-rose-400 bg-rose-50' : 'border-gray-200 bg-white focus:border-cyan-400'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPw(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showPw ? (lang === 'tr' ? 'Gizle' : 'Hide') : (lang === 'tr' ? 'Göster' : 'Show')}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            {errors.password && (
              <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> {errors.password}
              </p>
            )}
          </div>
        )}
      </div>

      {/* KVKK — only for student */}
      {selectedRole === 'student' && (
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={!!info.kvkk_consent}
              onChange={e => setInfo(prev => ({ ...prev, kvkk_consent: e.target.checked }))}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                info.kvkk_consent ? 'bg-cyan-500 border-cyan-500'
                : errors.kvkk_consent ? 'border-rose-400 bg-rose-50'
                : 'border-gray-300 group-hover:border-cyan-400'
              }`}
              onClick={() => setInfo(prev => ({ ...prev, kvkk_consent: !prev.kvkk_consent }))}
            >
              {info.kvkk_consent && <Check size={13} className="text-white" />}
            </div>
          </div>
          <span className="text-sm text-gray-700 leading-snug">
            {lang === 'tr' ? 'KVKK kapsamında kişisel verilerin işlenmesine onay veriyorum.' : 'I consent to processing of personal data under KVKK.'} <span className="text-rose-500">*</span>
          </span>
        </label>
      )}
      {errors.kvkk_consent && (
        <p className="text-xs text-rose-600 -mt-2 flex items-center gap-1 ml-8">
          <AlertCircle size={12} /> {errors.kvkk_consent}
        </p>
      )}
    </div>
  );
}

// ── Step 2: Main Course ────────────────────────────────────────────────────────

function MainCourseStep({ mainCourse, setMainCourse, courses, lang }) {
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const filtered = courses.filter(c =>
    (!search || (c.title || '').toLowerCase().includes(search.toLowerCase()) || (c.title_tr || '').toLowerCase().includes(search.toLowerCase())) &&
    (!filterCategory || c.category === filterCategory)
  );

  return (
    <div className="card-clay p-6 space-y-4">
      <h3 className="font-display text-xl font-bold flex items-center gap-2">
        <BookOpen size={22} className="text-emerald-500" />
        {lang === 'tr' ? 'Ana Eğitim Seçimi' : 'Main Course Selection'}
      </h3>
      <p className="text-sm text-[var(--text-muted)]">
        {lang === 'tr' ? 'Öğrencinin kayıt olacağı ana eğitimi seçin' : 'Select the main course for the student'}
      </p>

      {mainCourse && (
        <div className="bg-emerald-50 rounded-xl p-4 flex items-center gap-3 border-2 border-emerald-200">
          <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm truncate">{mainCourse.title || mainCourse.title_tr}</p>
            <p className="text-[10px] text-[var(--text-muted)]">{mainCourse.category}</p>
          </div>
          <span className="font-bold text-[var(--brand-primary)] flex-shrink-0">{mainCourse.price} TRY</span>
          <button onClick={() => setMainCourse(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
            <Trash2 size={14} />
          </button>
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={lang === 'tr' ? 'Kurs ara...' : 'Search courses...'}
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm font-semibold focus:border-cyan-400 focus:outline-none"
          data-testid="search-main-course"
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setFilterCategory('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold ${!filterCategory ? 'bg-[var(--brand-primary)] text-white' : 'bg-gray-100 text-[var(--text-muted)]'}`}
        >
          {lang === 'tr' ? 'Tümü' : 'All'}
        </button>
        {categoryFilters.map(cat => (
          <button
            key={cat.id}
            onClick={() => setFilterCategory(filterCategory === cat.id ? '' : cat.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${filterCategory === cat.id ? 'bg-sky-500 text-white' : 'bg-gray-100 text-[var(--text-muted)]'}`}
          >
            {lang === 'tr' ? cat.label_tr : cat.label}
          </button>
        ))}
      </div>

      <div className="border-2 border-gray-200 rounded-xl max-h-72 overflow-y-auto">
        {filtered.length === 0 && (
          <p className="p-6 text-center text-sm text-gray-400">{lang === 'tr' ? 'Kurs bulunamadı' : 'No courses found'}</p>
        )}
        {filtered.map(c => {
          const selected = mainCourse?.course_id === c.course_id;
          return (
            <button
              key={c.course_id}
              onClick={() => setMainCourse(c)}
              className={`w-full flex items-center gap-3 p-3 border-b last:border-b-0 text-left transition-colors ${selected ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
              data-testid={`course-${c.course_id}`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                {selected && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
              <BookOpen size={16} className={selected ? 'text-emerald-500' : 'text-gray-400'} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{c.title || c.title_tr}</p>
                <p className="text-xs text-gray-400">{c.category} — {c.level}</p>
              </div>
              <span className="text-sm font-bold text-gray-700 flex-shrink-0">{c.price} TRY</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 3: Additional Courses ────────────────────────────────────────────────

function AdditionalCoursesStep({ mainCourse, additionalCourses, setAdditionalCourses, courses, lang }) {
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const available = courses.filter(c =>
    c.course_id !== mainCourse?.course_id &&
    !additionalCourses.some(a => a.course_id === c.course_id) &&
    (!search || (c.title || '').toLowerCase().includes(search.toLowerCase()) || (c.title_tr || '').toLowerCase().includes(search.toLowerCase())) &&
    (!filterCategory || c.category === filterCategory)
  );

  return (
    <div className="card-clay p-6 space-y-4">
      <h3 className="font-display text-xl font-bold flex items-center gap-2">
        <Plus size={22} className="text-purple-500" />
        {lang === 'tr' ? 'Ek Eğitimler' : 'Additional Courses'}
      </h3>
      <p className="text-sm text-[var(--text-muted)]">
        {lang === 'tr' ? 'Opsiyonel — bu adımı atlayabilirsiniz' : 'Optional — you may skip this step'}
      </p>

      {additionalCourses.length > 0 && (
        <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
          <h4 className="text-xs font-bold text-purple-600 uppercase mb-2">
            {lang === 'tr' ? `Ek Eğitimler (${additionalCourses.length})` : `Additional (${additionalCourses.length})`}
          </h4>
          {additionalCourses.map((c, i) => (
            <div key={c.course_id} className="flex items-center gap-3 py-2 border-b border-purple-100 last:border-0">
              <span className="text-sm font-bold flex-1 truncate">{c.title || c.title_tr}</span>
              <input
                type="number"
                value={c.price_override !== undefined ? c.price_override : c.price}
                onChange={e => {
                  const arr = [...additionalCourses];
                  arr[i] = { ...arr[i], price_override: parseFloat(e.target.value) || 0 };
                  setAdditionalCourses(arr);
                }}
                className="w-20 px-2 py-1 rounded-lg border border-purple-200 text-sm font-bold text-right"
              />
              <span className="text-[10px] text-gray-500">TRY</span>
              <button onClick={() => setAdditionalCourses(additionalCourses.filter(x => x.course_id !== c.course_id))} className="text-red-400 hover:text-red-600">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={lang === 'tr' ? 'Ek kurs ara...' : 'Search additional courses...'}
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm font-semibold focus:border-cyan-400 focus:outline-none"
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setFilterCategory('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold ${!filterCategory ? 'bg-purple-500 text-white' : 'bg-gray-100 text-[var(--text-muted)]'}`}
        >
          {lang === 'tr' ? 'Tümü' : 'All'}
        </button>
        {categoryFilters.map(cat => (
          <button
            key={cat.id}
            onClick={() => setFilterCategory(filterCategory === cat.id ? '' : cat.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${filterCategory === cat.id ? 'bg-purple-500 text-white' : 'bg-gray-100 text-[var(--text-muted)]'}`}
          >
            {lang === 'tr' ? cat.label_tr : cat.label}
          </button>
        ))}
      </div>

      <div className="border-2 border-gray-200 rounded-xl max-h-60 overflow-y-auto">
        {available.length === 0 && (
          <p className="p-6 text-center text-sm text-gray-400">{lang === 'tr' ? 'Kurs bulunamadı' : 'No courses found'}</p>
        )}
        {available.map(c => (
          <button
            key={c.course_id}
            onClick={() => setAdditionalCourses(prev => [...prev, { ...c, price_override: c.price }])}
            className="w-full flex items-center gap-3 p-3 border-b last:border-b-0 text-left hover:bg-purple-50 transition-colors"
          >
            <Plus size={16} className="text-purple-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{c.title || c.title_tr}</p>
              <p className="text-xs text-gray-400">{c.category}</p>
            </div>
            <span className="text-sm font-bold text-purple-600 flex-shrink-0">{c.price} TRY</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 4: Certificate ────────────────────────────────────────────────────────

function CertificateStep({ selectedCertificate, setSelectedCertificate, certificates, lang }) {
  return (
    <div className="card-clay p-6 space-y-4">
      <h3 className="font-display text-xl font-bold flex items-center gap-2">
        <Award size={22} className="text-amber-500" />
        {lang === 'tr' ? 'Sertifika Seçimi' : 'Certificate Selection'}
      </h3>
      <p className="text-sm text-[var(--text-muted)]">
        {lang === 'tr' ? 'Opsiyonel — sertifika seçmeden devam edebilirsiniz' : 'Optional — you may skip this step'}
      </p>
      <div className="grid gap-3">
        {certificates.map(cert => {
          const selected = selectedCertificate?.cert_id === cert.cert_id;
          return (
            <button
              key={cert.cert_id}
              onClick={() => setSelectedCertificate(selected ? null : cert)}
              className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                selected ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-300 bg-white'
              }`}
              data-testid={`cert-${cert.cert_id}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selected ? 'bg-amber-400' : 'bg-gray-100'}`}>
                <Award size={24} className={selected ? 'text-white' : 'text-gray-400'} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm">{cert.name}</p>
                <p className="text-[10px] text-[var(--text-muted)]">{cert.provider}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'Sınav Ücreti' : 'Exam Fee'}</p>
                <p className="font-bold text-[var(--brand-primary)]">{cert.exam_fee || 0} TRY</p>
              </div>
              {selected && <CheckCircle size={20} className="text-amber-500 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 5: Payment + Summary ─────────────────────────────────────────────────

function PaymentStep({
  info, mainCourse, additionalCourses, selectedCertificate, payment, setPayment, createdStudent, lang,
  banks, banksLoading, saving, paymentPlanResetKey, onPaymentPlanChange,
}) {
  const allCourses = mainCourse ? [{ ...mainCourse, price_override: mainCourse.price }, ...additionalCourses] : [...additionalCourses];
  const coursesTotal = allCourses.reduce((sum, c) => sum + (c.price_override !== undefined ? c.price_override : (c.price || 0)), 0);
  const examFee = !payment.exam_fee_included && selectedCertificate ? (payment.exam_fee || 0) : 0;
  const totalAmount = coursesTotal + examFee;
  const discountedTotal = payment.assignment_type === 'free' ? 0 : totalAmount * (1 - (payment.discount_percent || 0) / 100);

  // Satış anında POS hesabı tahsilat hesabı olarak KABUL EDİLMEZ — yalnızca
  // kasa/banka listelenir (bkz. lib/posNetAmount.js).
  const collectionBanks = (banks || []).filter((b) => !isPosAccountType(b.account_type));

  return (
    <div className="card-clay p-6 space-y-5">
      <h3 className="font-display text-xl font-bold flex items-center gap-2">
        <CreditCard size={22} className="text-[var(--brand-primary)]" />
        {lang === 'tr' ? 'Özet ve Ödeme' : 'Summary & Payment'}
      </h3>

      {/* Summary */}
      <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">{lang === 'tr' ? 'Kayıt Özeti' : 'Summary'}</h4>
        {createdStudent && (
          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200">
            <div className="w-8 h-8 rounded-full bg-[var(--brand-primary)] flex items-center justify-center">
              <span className="text-white text-xs font-bold">{(createdStudent.name || info.first_name || '?')[0]}</span>
            </div>
            <div>
              <p className="text-sm font-bold">{createdStudent.name || `${info.first_name} ${info.last_name}`}</p>
              <p className="text-xs text-[var(--text-muted)]">{createdStudent.email || info.email}</p>
            </div>
          </div>
        )}
        {mainCourse && (
          <div className="flex justify-between py-1.5 text-sm">
            <span className="font-semibold flex items-center gap-1"><BookOpen size={12} className="text-emerald-500" /> {mainCourse.title || mainCourse.title_tr}</span>
            <span className="font-bold">{(mainCourse.price || 0).toFixed(2)} TRY</span>
          </div>
        )}
        {additionalCourses.map(c => (
          <div key={c.course_id} className="flex justify-between py-1.5 text-sm">
            <span className="font-semibold flex items-center gap-1 text-purple-600"><Plus size={12} /> {c.title || c.title_tr}</span>
            <span className="font-bold">{((c.price_override !== undefined ? c.price_override : c.price) || 0).toFixed(2)} TRY</span>
          </div>
        ))}
        <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between text-sm">
          <span className="font-bold">{lang === 'tr' ? 'Eğitim Toplamı' : 'Course Total'}</span>
          <span className="font-bold">{coursesTotal.toFixed(2)} TRY</span>
        </div>
      </div>

      {/* Assignment type */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { v: 'paid', icon: CreditCard, label: lang === 'tr' ? 'Ücretli' : 'Paid', colorOn: 'border-cyan-500 bg-cyan-50 text-cyan-700' },
          { v: 'free', icon: Gift, label: lang === 'tr' ? 'Ücretsiz' : 'Free', colorOn: 'border-emerald-500 bg-emerald-50 text-emerald-700' },
        ].map(({ v, icon: Icon, label, colorOn }) => (
          <button
            key={v}
            type="button"
            onClick={() => setPayment(p => ({ ...p, assignment_type: v }))}
            className={`flex items-center gap-2 p-3 rounded-xl border-2 text-sm font-bold transition-all ${payment.assignment_type === v ? colorOn : 'border-gray-200 bg-white text-gray-500'}`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {payment.assignment_type === 'paid' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-gray-600 mb-1 block">{lang === 'tr' ? 'İndirim %' : 'Discount %'}</label>
            <input
              type="number" min="0" max="100"
              value={payment.discount_percent || 0}
              onChange={e => setPayment(p => ({ ...p, discount_percent: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm font-bold focus:border-cyan-400 focus:outline-none"
            />
          </div>

          {/* FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan, bkz.
              components/sales/PaymentPlanFields.jsx dosya başı yorumu. */}
          <PaymentPlanFields
            key={paymentPlanResetKey}
            role="admin"
            totalAmount={discountedTotal}
            banks={collectionBanks}
            banksLoading={banksLoading}
            disabled={saving}
            onChange={onPaymentPlanChange}
          />

          <div>
            <label className="text-xs font-bold text-gray-600 mb-1 block">{lang === 'tr' ? 'Not' : 'Notes'}</label>
            <input
              value={payment.notes || ''}
              onChange={e => setPayment(p => ({ ...p, notes: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm font-semibold focus:border-cyan-400 focus:outline-none"
            />
          </div>
        </div>
      )}

      {/* Grand total */}
      <div className="bg-[#E8F5FD] rounded-xl p-4">
        {payment.assignment_type === 'paid' ? (
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--text-muted)]">{lang === 'tr' ? 'Ara Toplam' : 'Subtotal'}</span>
              <span className="font-bold">{coursesTotal.toFixed(2)} TRY</span>
            </div>
            {(payment.discount_percent || 0) > 0 && (
              <div className="flex justify-between text-emerald-600">
                <span>{lang === 'tr' ? 'İndirim' : 'Discount'} (%{payment.discount_percent})</span>
                <span>-{(totalAmount * (payment.discount_percent / 100)).toFixed(2)} TRY</span>
              </div>
            )}
            <div className="flex justify-between items-center border-t border-sky-200 mt-2 pt-2">
              <span className="font-bold">{lang === 'tr' ? 'Ödenecek' : 'Total Due'}</span>
              <span className="font-display text-2xl font-bold text-[var(--brand-primary)]">{discountedTotal.toFixed(2)} TRY</span>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center font-bold text-emerald-600">
            <span>{lang === 'tr' ? 'Ücretsiz Atama' : 'Free Assignment'}</span>
            <span className="font-display text-2xl">0.00 TRY</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ currentStep, steps, lang: _lang }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8 overflow-x-auto pb-1" data-testid="step-indicator">
      {steps.map((s, i) => {
        const isActive = currentStep === i;
        const isComplete = i < currentStep;
        return (
          <div key={i} className="flex items-center">
            {i > 0 && (
              <div className={`w-6 lg:w-12 h-1 mx-0.5 rounded-full ${isComplete ? 'bg-emerald-400' : 'bg-gray-200'}`} />
            )}
            <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap ${
              isActive ? 'bg-[var(--brand-primary)] text-white shadow-lg'
              : isComplete ? 'bg-emerald-500 text-white'
              : 'bg-gray-100 text-[var(--text-muted)]'
            }`}>
              {isComplete ? <Check size={14} /> : <span>{i + 1}</span>}
              <span className="hidden lg:inline">{s}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function NewUserPage() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Bu sayfa yalnızca /admin/users/new (admin/superadmin, RoleRoute) altında
  // render edilir — GET /admin/banks require_admin, her zaman açık.
  const { data: banks = [], isLoading: banksLoading } = useAccountingBanks();

  // Role from URL query ?role=student
  const queryRole = searchParams.get('role');

  // If role is pre-selected via URL, skip step 0
  const [selectedRole, setSelectedRole] = useState(queryRole || null);
  const [step, setStep] = useState(queryRole ? 1 : 0);

  const [info, setInfo] = useState(INITIAL_INFO);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const [mainCourse, setMainCourse] = useState(null);
  const [additionalCourses, setAdditionalCourses] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [payment, setPayment] = useState(INITIAL_PAYMENT);
  // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları/doğrulaması
  // artık paylaşılan components/sales/PaymentPlanFields.jsx tarafından
  // yönetilir (bkz. o dosyanın başı yorumu).
  const [paymentPlanFields, setPaymentPlanFields] = useState({});
  const [paymentPlanValid, setPaymentPlanValid] = useState(false);
  const [paymentPlanResetKey, setPaymentPlanResetKey] = useState(0);
  const handlePaymentPlanChange = useCallback((fields, meta) => {
    setPaymentPlanFields(fields);
    setPaymentPlanValid(meta.isValid);
  }, []);

  const [createdStudent, setCreatedStudent] = useState(null);
  const [step1Done, setStep1Done] = useState(false);
  const [result, setResult] = useState(null);

  // Load courses + cert types on mount
  useEffect(() => {
    axios.get(`${API}/courses?limit=200`).then(r => setCourses(r.data.courses || [])).catch(() => {});
    axios.get(`${API}/admin/certificates/types`, { headers: authHeaders() })
      .then(r => setCertificates(r.data.certificates || []))
      .catch(() => {
        setCertificates([
          { cert_id: 'meb', name: 'MEB Onaylı', name_en: 'MEB Approved', provider: 'MEB', exam_fee: 500 },
          { cert_id: 'uni_istanbul', name: 'İstanbul Üniversitesi', name_en: 'Istanbul University', provider: 'Istanbul Uni', exam_fee: 750 },
          { cert_id: 'uni_anadolu', name: 'Anadolu Üniversitesi', name_en: 'Anadolu University', provider: 'Anadolu Uni', exam_fee: 600 },
          { cert_id: 'international', name: 'Uluslararası Sertifika', name_en: 'International Certificate', provider: 'IATA', exam_fee: 1200 },
        ]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoleSelect = useCallback((role) => {
    setSelectedRole(role);
    setStep(1);
  }, []);

  const validate1 = useCallback(() => {
    const errs = {};
    if (!info.first_name?.trim()) errs.first_name = lang === 'tr' ? 'Zorunlu alan' : 'Required';
    if (!info.last_name?.trim()) errs.last_name = lang === 'tr' ? 'Zorunlu alan' : 'Required';
    if (!info.email?.trim()) errs.email = lang === 'tr' ? 'Zorunlu alan' : 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email)) errs.email = lang === 'tr' ? 'Geçerli e-posta girin' : 'Invalid email';
    if (!info.phone?.trim()) errs.phone = lang === 'tr' ? 'Zorunlu alan' : 'Required';
    if (selectedRole === 'student') {
      if (!info.tc_no?.trim()) errs.tc_no = lang === 'tr' ? 'Zorunlu alan' : 'Required';
      else if (!/^\d{11}$/.test(info.tc_no.trim())) errs.tc_no = lang === 'tr' ? '11 rakam olmalı' : 'Must be 11 digits';
      if (!info.kvkk_consent) errs.kvkk_consent = lang === 'tr' ? 'KVKK onayı zorunlu' : 'KVKK consent required';
    }
    if (!info.autoGenPassword && !info.password?.trim()) errs.password = lang === 'tr' ? 'Şifre zorunlu' : 'Password required';
    return errs;
  }, [info, lang, selectedRole]);

  const registerUser = async () => {
    if (step1Done) return true; // already registered
    setSaving(true);
    try {
      const pw = info.autoGenPassword ? info.generatedPassword : info.password;
      const payload = {
        first_name: info.first_name.trim(),
        last_name: info.last_name.trim(),
        email: info.email.trim(),
        phone: info.phone.trim(),
        role: selectedRole,
        kvkk_consent: selectedRole === 'student' ? true : undefined,
        tos_accepted: true,
        ...(pw ? { password: pw } : {}),
        ...(info.tc_no?.trim() ? { tc_no: info.tc_no.trim() } : {}),
        ...(info.birth_date ? { birth_date: info.birth_date } : {}),
        ...(info.address?.trim() ? { address: info.address.trim() } : {}),
        ...(info.city?.trim() ? { city: info.city.trim() } : {}),
        ...(info.district?.trim() ? { district: info.district.trim() } : {}),
        ...(info.postal_code?.trim() ? { postal_code: info.postal_code.trim() } : {}),
        ...(info.emergency_contact?.trim() ? { emergency_contact: info.emergency_contact.trim() } : {}),
        ...(info.emergency_phone?.trim() ? { emergency_phone: info.emergency_phone.trim() } : {}),
        ...(info.notes?.trim() ? { notes: info.notes.trim() } : {}),
      };
      // Remove undefined keys
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const res = await axios.post(`${API}/admin/students/register`, payload, { headers: authHeaders() });
      const userData = res.data;
      setCreatedStudent({
        ...userData,
        generatedPassword: info.autoGenPassword ? info.generatedPassword : undefined,
        generated_password: userData.generated_password || (info.autoGenPassword ? info.generatedPassword : undefined),
      });
      setStep1Done(true);
      return true;
    } catch (err) {
      const msg = getApiErrorMessage(err, lang) || (lang === 'tr' ? 'Kayıt başarısız' : 'Registration failed');
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    setError('');
    setErrors({});

    if (step === 1) {
      const errs = validate1();
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }
      // For non-student roles → register and go to success
      if (selectedRole !== 'student') {
        const ok = await registerUser();
        if (!ok) return;
        setResult({ enrolled_courses: [] });
        return;
      }
      // Student → register then go to step 2
      const ok = await registerUser();
      if (!ok) return;
      setStep(2);
    } else if (step === 2) {
      if (!mainCourse) {
        setError(lang === 'tr' ? 'Ana eğitim seçin' : 'Select main course');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      setStep(5);
    }
  };

  const handleBack = () => {
    setError('');
    if (step === 1) {
      setSelectedRole(null);
      setStep(0);
    } else {
      setStep(s => s - 1);
    }
  };

  const handleFinalize = async () => {
    if (!createdStudent) return;
    setSaving(true);
    setError('');
    try {
      const allCourses = mainCourse ? [{ ...mainCourse, price_override: mainCourse.price }, ...additionalCourses] : [...additionalCourses];
      const res = await axios.post(
        `${API}/admin/students/${createdStudent.user_id}/enroll-courses`,
        {
          courses: allCourses.map(c => ({
            course_id: c.course_id,
            price_override: c.price_override !== undefined ? c.price_override : c.price,
          })),
          assignment_type: payment.assignment_type,
          payment_notes: payment.notes,
          discount_percent: payment.assignment_type === 'free' ? 0 : (payment.discount_percent || 0),
          certificate_type: selectedCertificate?.name || null,
          certificate_provider: selectedCertificate?.provider || null,
          // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları
          // (down_payment, down_payment_method/bank_id, installments/
          // first_due_date VEYA installment_plan, payment_method)
          // PaymentPlanFields tarafından hesaplanır (bkz. yukarıdaki onChange).
          // Ücretsiz atamada bunların hiçbiri gönderilmez.
          ...(payment.assignment_type === 'free' ? { payment_method: 'free' } : paymentPlanFields),
        },
        { headers: authHeaders() }
      );
      setResult(res.data);
      toast.success(lang === 'tr' ? 'Kayıt tamamlandı' : 'Registration complete');
    } catch (err) {
      const msg = getApiErrorMessage(err, lang) || (lang === 'tr' ? 'Kayıt başarısız' : 'Enrollment failed');
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setStep(queryRole ? 1 : 0);
    setSelectedRole(queryRole || null);
    setInfo(INITIAL_INFO);
    setErrors({});
    setError('');
    setCreatedStudent(null);
    setStep1Done(false);
    setMainCourse(null);
    setAdditionalCourses([]);
    setSelectedCertificate(null);
    setPayment(INITIAL_PAYMENT);
    setPaymentPlanFields({});
    setPaymentPlanValid(false);
    setPaymentPlanResetKey((k) => k + 1);
    setResult(null);
  };

  // Step labels
  const studentSteps = [
    lang === 'tr' ? 'Kişisel Bilgi' : 'Personal Info',
    lang === 'tr' ? 'Ana Kurs' : 'Main Course',
    lang === 'tr' ? 'Ek Kurslar' : 'Additional',
    lang === 'tr' ? 'Sertifika' : 'Certificate',
    lang === 'tr' ? 'Özet' : 'Summary',
  ];
  const otherSteps = [lang === 'tr' ? 'Kişisel Bilgi' : 'Personal Info'];
  const steps = selectedRole === 'student' ? studentSteps : otherSteps;

  // FAZ 1 karma ödeme turu (2026-09-25) — PaymentStep içindeki
  // PaymentPlanFields kendi geçerliliğini (peşinat/kalan/banka seçimi)
  // meta.isValid ile bildirir; "Kaydı Tamamla" butonu ücretli atamada BUNA
  // kilitlenir (backend zaten kendi kurallarıyla 400 döner — bu yalnızca
  // arayüz kolaylığı).
  const finalizePaymentInvalid = payment.assignment_type === 'paid' && !paymentPlanValid;

  const pageTitle = selectedRole === 'student'
    ? (lang === 'tr' ? 'Yeni Öğrenci Kaydı' : 'New Student Registration')
    : selectedRole === 'instructor'
    ? (lang === 'tr' ? 'Yeni Eğitmen' : 'New Instructor')
    : selectedRole === 'admin'
    ? (lang === 'tr' ? 'Yeni Admin' : 'New Admin')
    : (lang === 'tr' ? 'Yeni Kullanıcı' : 'New User');

  // Success state
  if (result) {
    return (
      <AdminLayout title={pageTitle} titleTr={pageTitle}>
        <SuccessScreen
          createdUser={createdStudent}
          enrollResult={result}
          selectedRole={selectedRole}
          lang={lang}
          authHeaders={authHeaders}
          onReset={handleReset}
          navigate={navigate}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={pageTitle} titleTr={pageTitle}>
      <div className="max-w-3xl mx-auto">
        {/* Step 0: Role selection */}
        {step === 0 && (
          <RoleStep lang={lang} onSelect={handleRoleSelect} />
        )}

        {/* Steps 1-5 */}
        {step >= 1 && (
          <>
            <StepIndicator currentStep={step - 1} steps={steps} lang={lang} />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-600 font-semibold">
                <AlertCircle size={18} /> {error}
              </div>
            )}

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
                  <PersonalInfoStep info={info} setInfo={setInfo} errors={errors} lang={lang} selectedRole={selectedRole} />
                </motion.div>
              )}
              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
                  <MainCourseStep mainCourse={mainCourse} setMainCourse={setMainCourse} courses={courses} lang={lang} />
                </motion.div>
              )}
              {step === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
                  <AdditionalCoursesStep
                    mainCourse={mainCourse}
                    additionalCourses={additionalCourses}
                    setAdditionalCourses={setAdditionalCourses}
                    courses={courses}
                    lang={lang}
                  />
                </motion.div>
              )}
              {step === 4 && (
                <motion.div key="step4" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
                  <CertificateStep
                    selectedCertificate={selectedCertificate}
                    setSelectedCertificate={setSelectedCertificate}
                    certificates={certificates}
                    lang={lang}
                  />
                </motion.div>
              )}
              {step === 5 && (
                <motion.div key="step5" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
                  <PaymentStep
                    info={info}
                    mainCourse={mainCourse}
                    additionalCourses={additionalCourses}
                    selectedCertificate={selectedCertificate}
                    payment={payment}
                    setPayment={setPayment}
                    createdStudent={createdStudent}
                    lang={lang}
                    banks={banks}
                    banksLoading={banksLoading}
                    saving={saving}
                    paymentPlanResetKey={paymentPlanResetKey}
                    onPaymentPlanChange={handlePaymentPlanChange}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBack}
                disabled={saving}
                className="btn-tactile px-6 py-3 bg-white border-[var(--border-light)] disabled:opacity-30 flex items-center gap-2"
                data-testid="prev-step-btn"
              >
                <ChevronLeft size={18} />
                {lang === 'tr' ? 'Geri' : 'Back'}
              </motion.button>

              {step < 5 || selectedRole !== 'student' ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNext}
                  disabled={saving}
                  className="btn-primary px-8 py-3 flex items-center gap-2 disabled:opacity-50"
                  data-testid="next-step-btn"
                >
                  {saving
                    ? (lang === 'tr' ? 'Kaydediliyor...' : 'Saving...')
                    : (lang === 'tr' ? 'Devam Et' : 'Continue')}
                  <ChevronRight size={18} />
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleFinalize}
                  disabled={saving || finalizePaymentInvalid}
                  className="btn-success px-8 py-3 flex items-center gap-2 disabled:opacity-50"
                  data-testid="finalize-btn"
                >
                  {saving
                    ? (lang === 'tr' ? 'Tamamlanıyor...' : 'Completing...')
                    : (lang === 'tr' ? 'Kaydı Tamamla' : 'Complete')}
                  <CheckCircle size={18} />
                </motion.button>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
